import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { TSignatureCampaign, TSignatureCampaignAudience } from './interface'

const resource = 'signatureCampaigns'
const base = () => `${BASE_URL}/${API_MAIN_VERSION}/${resource}`

type TListQuery = { page?: number, limit?: number, search?: string, status?: string }

/** Grade, office and grade category are read on each signer's current job. */
type TSignerListQuery = TListQuery & {
  resources?: string[]
  offices?: string[]
  jobs?: string[]
  jobCategories?: string[]
  excludedResources?: string[]
  excludedOffices?: string[]
  excludedJobs?: string[]
  excludedJobCategories?: string[]
}

const list = (axios: IFactoryFunctionRequest<any, any>) => (query: TListQuery = {}) => axios({
  method: EMethod.get,
  url: base(),
  query,
})

const getOne = (axios: IFactoryFunctionRequest<TSignatureCampaign, any>) => (id: string) => axios({
  method: EMethod.get,
  url: `${base()}/${id}`,
})

const create = (axios: IFactoryFunctionRequest<TSignatureCampaign, any>) => (data: Partial<TSignatureCampaign>) => axios({
  method: EMethod.post,
  url: base(),
  data,
})

const update = (axios: IFactoryFunctionRequest<TSignatureCampaign, any>) => (id: string, data: Partial<TSignatureCampaign>) => axios({
  method: EMethod.put,
  url: `${base()}/${id}`,
  data,
})

const remove = (axios: IFactoryFunctionRequest<any, any>) => (id: string) => axios({
  method: EMethod.delete,
  url: `${base()}/${id}`,
})

/**
 * `id` is optional: a campaign being composed in the creation modal has none yet.
 * `withChanges`: on a launched campaign, also count what saving this audience would change.
 */
const previewAudience = (axios: IFactoryFunctionRequest<any, any>) =>
  (audience: TSignatureCampaignAudience, id?: string, options: { search?: string, limit?: number, withChanges?: boolean } = {}) => axios({
    method: EMethod.post,
    url: id ? `${base()}/${id}/audience/preview` : `${base()}/audience/preview`,
    data: { audience, ...options },
    immediate: false,
  })

const launch = (axios: IFactoryFunctionRequest<TSignatureCampaign, any>) => (id: string) => axios({
  method: EMethod.post,
  url: `${base()}/${id}/launch`,
  immediate: false,
})

const close = (axios: IFactoryFunctionRequest<TSignatureCampaign, any>) => (id: string) => axios({
  method: EMethod.post,
  url: `${base()}/${id}/close`,
  immediate: false,
})

const listSigners = (axios: IFactoryFunctionRequest<any, any>) => (id: string, query: TSignerListQuery = {}) => axios({
  method: EMethod.get,
  url: `${base()}/${id}/signers`,
  query,
})

/** `scope`: `audience` lists who newly matches it, `all` anybody in the admin's perimeter not enrolled yet. */
type TEligibleQuery = { search?: string, scope?: 'audience' | 'all', limit?: number }

const listEligibleSigners = (axios: IFactoryFunctionRequest<any, any>) => (id: string, query: TEligibleQuery = {}) => axios({
  method: EMethod.get,
  url: `${base()}/${id}/signers/eligible`,
  query,
  immediate: false,
})

/** `sendAt` holds their document back until then; null sends it right away (or with the campaign). */
const addSigners = (axios: IFactoryFunctionRequest<any, any>) => (id: string, resourceIds: string[], sendAt: string | null = null) => axios({
  method: EMethod.post,
  url: `${base()}/${id}/signers`,
  data: { resourceIds, sendAt },
  immediate: false,
})

const signerAction = (action: string) => (axios: IFactoryFunctionRequest<any, any>) =>
  (id: string, signerIds: string[], extra: Record<string, any> = {}) => axios({
    method: EMethod.post,
    url: `${base()}/${id}/signers/${action}`,
    data: { signerIds, ...extra },
    immediate: false,
  })

const exportSigners = (axios: IFactoryFunctionRequest<any, any>) => (id: string, query: { locale?: string } = {}) => axios({
  method: EMethod.get,
  url: `${base()}/${id}/export`,
  query,
  immediate: false,
})

const exportDocuments = (axios: IFactoryFunctionRequest<Blob, any>) => (id: string, query: { locale?: string } = {}) => axios({
  method: EMethod.get,
  url: `${base()}/${id}/export-documents`,
  query,
  responseType: 'blob',
  immediate: false,
})

const getMine = (axios: IFactoryFunctionRequest<any, any>) => () => axios({
  method: EMethod.get,
  url: `${base()}/mine`,
})

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  remove: remove(useAxios(axios)),
  previewAudience: previewAudience(useAxios(axios)),
  launch: launch(useAxios(axios)),
  close: close(useAxios(axios)),
  listSigners: listSigners(useAxios(axios)),
  listEligibleSigners: listEligibleSigners(useAxios(axios)),
  addSigners: addSigners(useAxios(axios)),
  exemptSigners: signerAction('exempt')(useAxios(axios)),
  reinstateSigners: signerAction('reinstate')(useAxios(axios)),
  remindSigners: signerAction('remind')(useAxios(axios)),
  retrySigners: signerAction('retry')(useAxios(axios)),
  archiveSigners: signerAction('archive')(useAxios(axios)),
  exportSigners: exportSigners(useAxios(axios)),
  exportDocuments: exportDocuments(useAxios(axios)),
  getMine: getMine(useAxios(axios)),
})
